import LoaderAnimation from "@/assets/animations/Loader.json";
import { Overlay, useTheme } from "@rneui/themed";
import Lottie from "lottie-react";
import LottieView from "lottie-react-native";
import React, { useEffect, useRef, useState } from "react";
import { Modal, Platform, StatusBar, StyleSheet, View } from "react-native";

const HIDE_DEBOUNCE_MS = 300;

type ScreenLoaderProps = {
  /** When provided, controls visibility with debounced hide to prevent flicker from rapid loading toggles. Omit for always-visible (e.g. Suspense fallback). */
  visible?: boolean;
};

const ScreenLoader = ({ visible = true }: ScreenLoaderProps) => {
  const { theme } = useTheme();
  const [debouncedVisible, setDebouncedVisible] = useState(visible);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (visible) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setDebouncedVisible(true);
      if (Platform.OS !== "web") {
        StatusBar.setHidden(false, "fade");
      }
    } else {
      hideTimeoutRef.current = setTimeout(() => {
        setDebouncedVisible(false);
        if (Platform.OS !== "web") {
          StatusBar.setHidden(false, "fade");
        }
        hideTimeoutRef.current = null;
      }, HIDE_DEBOUNCE_MS);
    }
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      if (Platform.OS !== "web") {
        StatusBar.setHidden(false, "fade");
      }
    };
  }, [visible]);

  if (!debouncedVisible) return null;

  // On web: use a fixed full-screen overlay via absoluteFillObject
  if (Platform.OS === "web") {
    return (
      <Overlay
        isVisible={debouncedVisible}
        backdropStyle={{
          backgroundColor: theme.colors.white,
        }}
        overlayStyle={{
          backgroundColor: theme.colors.white,
          padding: 0,
          margin: 0,
          borderRadius: 0,
          boxShadow: "none",
        }}
      >
        <Lottie
          animationData={LoaderAnimation}
          autoPlay
          loop
          style={{
            width: 700,
            height: 700,
            backgroundColor: "transparent",
          }}
        />
      </Overlay>
    );
  }

  // On native: Modal renders at the OS window level, completely outside the
  // React component tree / parent containers — no flicker, no positional shifts.
  return (
    <Modal
      visible={debouncedVisible}
      transparent={false}
      animationType="none"
      statusBarTranslucent
      hardwareAccelerated
    >
      <View style={[styles.overlay, { backgroundColor: theme.colors.white }]}>
        <LottieView
          source={LoaderAnimation}
          autoPlay
          loop
          style={{ width: 700, height: 700 }}
        />
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default ScreenLoader;
